import type { PendingFile } from '@/types/upload'
import { UPLOAD_STATUS_COLORS, formatFileSize } from '@/types/upload'

export interface FileItemProps {
  file: PendingFile
  onRemove: () => void
}

/**
 * Individual file item component showing file info, status, and progress.
 */
export function FileItem({ file, onRemove }: FileItemProps) {
  const statusColors = UPLOAD_STATUS_COLORS[file.status]
  const showProgress = file.status === 'uploading'
  const showRemove = file.status === 'pending' || file.status === 'error'
  const showError = file.status === 'error' && file.error
  const showDuplicate = file.status === 'duplicate'
  const showValidated = file.status === 'validated'

  return (
    <div
      className="flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-lg"
      data-testid="file-item"
      data-status={file.status}
    >
      {/* File icon */}
      <div className="flex-shrink-0">
        <svg
          className="w-8 h-8 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      </div>

      {/* File info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate" data-testid="file-name">
          {file.file.name}
        </p>
        <p className="text-xs text-gray-500" data-testid="file-size">
          {formatFileSize(file.file.size)}
        </p>

        {/* Progress bar */}
        {showProgress && (
          <div className="mt-2">
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div
                className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                style={{ width: `${file.progress}%` }}
                data-testid="progress-bar"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1" data-testid="progress-text">
              {file.progress}%
            </p>
          </div>
        )}

        {/* Error message */}
        {showError && (
          <p className="text-xs text-red-600 mt-1" data-testid="error-message">
            {file.error}
          </p>
        )}

        {/* Duplicate message */}
        {showDuplicate && (
          <p className="text-xs text-yellow-600 mt-1" data-testid="duplicate-message">
            {file.result?.message || 'This document already exists'}
          </p>
        )}

        {/* Validated (verified) message — document was protected from reprocessing */}
        {showValidated && (
          <p className="text-xs text-emerald-700 mt-1" data-testid="validated-message">
            {file.error || 'This document is already verified and was not reprocessed'}
          </p>
        )}
      </div>

      {/* Status badge */}
      <div className="flex-shrink-0">
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors}`}
          data-testid="status-badge"
        >
          {getStatusLabel(file.status)}
        </span>
      </div>

      {/* Remove button */}
      {showRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
          data-testid="remove-button"
          aria-label={`Remove ${file.file.name}`}
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      )}
    </div>
  )
}

function getStatusLabel(status: PendingFile['status']): string {
  const labels: Record<PendingFile['status'], string> = {
    pending:      'Pending',
    uploading:    'Uploading',
    success:      'Uploaded',
    error:        'Failed',
    duplicate:    'Duplicate',
    reprocessing: 'Reprocessing...',
    validated:    'Verified — not reprocessed',
  }
  return labels[status]
}

export default FileItem
